
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import SignUpForm from "@/components/auth/SignUpForm";
import { Loader2 } from "lucide-react";
import Logo from "@/components/navbar/Logo";

export default function SignUp() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(true);

  // If user is already logged in, redirect to home page
  useEffect(() => {
    if (!loading) {
      if (user) {
        // User is logged in, redirect to home
        console.log("User is already logged in, redirecting to home");
        navigate("/");
      } else {
        // User is not logged in, show the sign-up page
        console.log("User is not logged in, showing sign-up page");
      }
      // Set pageLoading to false once auth state is determined
      setPageLoading(false);
    }
  }, [user, loading, navigate]);

  // Show loading state during auth check
  if (loading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-center flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center">
            <div className="h-12 w-40 relative flex justify-center">
              <Logo textSize="text-2xl" linkTo={false} />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Create your account</h2>
          <p className="mt-2 text-sm text-gray-600">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500">
              Sign in
            </Link>
          </p>
        </div>

        <Card className="shadow-lg border-gray-200">
          <CardHeader>
            <CardTitle className="text-xl">Sign Up</CardTitle>
            <CardDescription>Fill in the details below to create your account</CardDescription>
          </CardHeader>
          <CardContent>
            <SignUpForm />
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-xs text-gray-500">
              By signing up, you agree to our <Link to="/terms" className="text-purple-600 hover:text-purple-500">Terms of Service</Link> and <Link to="/privacy" className="text-purple-600 hover:text-purple-500">Privacy Policy</Link>.
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
